import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChevronDown,
  Trash2,
  Search,
  FolderPlus,
  Folder as FolderIcon,
  MessageSquare,
  MoreVertical,
  Play,
  Loader2,
  AlertTriangle,
  Clapperboard,
  type LucideIcon,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/v2/utils';
import { apiService } from '@/api/apiService';
import { useUserStore } from '@/store/userStore';
import type { Folder, Order } from '@/types';
import { vConfirm, vPrompt } from '@/v2/dialogs';
import { toast } from '@/v2/toast';
import i18n from '@/i18n';

type ThumbTheme = { gradient: string; Icon: LucideIcon; label: string; spin?: boolean };

/**
 * Thumbnail cover theme, keyed STRICTLY by status (no more index-based gradients,
 * which made some cards stone-gray at random). Generated videos show their own
 * preview; every OTHER state gets a consistent colored cover so the grid reads
 * the same everywhere:  draft → purple/pink · rendering → orange · failed → red.
 * (Never gray.)
 */
function thumbTheme(status?: string): ThumbTheme {
  switch (status) {
    case 'in_progress':
    case 'pending':
      return { gradient: 'from-orange-400 via-orange-500 to-amber-600', Icon: Loader2, label: i18n.t('projects.rendering', { ns: 'v2' }), spin: true };
    case 'failed':
      return { gradient: 'from-red-500 via-rose-600 to-red-800', Icon: AlertTriangle, label: i18n.t('projects.failed', { ns: 'v2' }) };
    case 'completed':
      // Fallback only — completed videos render their own media on top of this.
      return { gradient: 'from-primary via-primary/70 to-slate-900', Icon: Play, label: '' };
    case 'chat_active':
    default:
      return { gradient: 'from-primary via-fuchsia-500 to-pink-500', Icon: Clapperboard, label: i18n.t('projects.draft', { ns: 'v2' }) };
  }
}

/** The themed status cover shown when there's no playable media (draft / rendering
 *  / failed). Consistent layout across all three — only the color + emblem change. */
function StatusCover({ theme }: { theme: ThumbTheme }) {
  const { Icon, label, spin } = theme;
  return (
    <>
      {/* faint "script lines" for texture (matches the old placeholder feel) */}
      <div className="absolute inset-0 p-6 flex flex-col justify-center gap-3 opacity-20">
        <div className="h-4 w-3/5 rounded-full bg-white/60" />
        <div className="h-4 w-2/5 rounded-full bg-white/50 self-end" />
        <div className="h-4 w-1/2 rounded-full bg-white/40" />
      </div>
      <div className="absolute inset-0 bg-black/10" />
      {/* centered emblem + label */}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2.5">
        <span className="w-12 h-12 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 flex items-center justify-center shadow-lg">
          <Icon className={cn('w-6 h-6 text-white', spin && 'animate-spin')} />
        </span>
        {label && <span className="text-[11px] font-bold text-white tracking-widest uppercase drop-shadow">{label}</span>}
      </div>
    </>
  );
}

function relativeTime(iso?: string): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const mins = Math.max(1, Math.round((Date.now() - then) / 60000));
  if (mins < 60) return i18n.t('projects.minutesAgo', { ns: 'v2', count: mins });
  const hours = Math.round(mins / 60);
  if (hours < 24) return i18n.t('projects.hoursAgo', { ns: 'v2', count: hours });
  const days = Math.round(hours / 24);
  if (days === 1) return i18n.t('projects.yesterday', { ns: 'v2' });
  return i18n.t('projects.daysAgo', { ns: 'v2', count: days });
}

function providerLabel(o: Order): string {
  if (o.operationType === 'heygen_chat') return i18n.t('projects.videoAgent', { ns: 'v2' });
  if (o.videoProvider === 'heygen') return i18n.t('projects.avatar', { ns: 'v2' });
  return o.operationType.replace(/_/g, ' ');
}

function statusBadge(o: Order): string | null {
  switch (o.orderStatus) {
    case 'chat_active': return i18n.t('projects.draft', { ns: 'v2' });        // job submitted, plan in chat
    case 'in_progress': return i18n.t('projects.rendering', { ns: 'v2' });
    case 'pending': return i18n.t('projects.rendering', { ns: 'v2' });
    case 'completed': return i18n.t('projects.success', { ns: 'v2' });
    case 'failed': return i18n.t('projects.failed', { ns: 'v2' });
    case 'preview': return i18n.t('projects.preview', { ns: 'v2' });
    default: return null;
  }
}

// Status colour dot — IDENTICAL to the agentic-chat projects sidebar
// (ProjectHistorySidebar.statusDotClass), so the status reads the same in both
// places. Rendered as a small dot beside the label under each project name.
function statusDotClass(status?: string): string {
  switch (status) {
    case 'completed': return 'bg-emerald-400';
    case 'in_progress':
    case 'pending': return 'bg-amber-400 animate-pulse';
    case 'failed': return 'bg-red-400';
    case 'preview': return 'bg-primary';
    default: return 'bg-text-tertiary';
  }
}

export function ProjectsPage() {
  const { t } = useTranslation('v2');
  const navigate = useNavigate();
  const userId = useUserStore((s) => s.userId);

  const [folders, setFolders] = useState<Folder[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeFolder, setActiveFolder] = useState<string | null>(null); // null = Recent (all)
  const [sortDir, setSortDir] = useState<'newest' | 'oldest'>('newest');
  const [statusFilter, setStatusFilter] = useState<'all' | 'drafts' | 'rendering' | 'done' | 'failed'>('all');
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  // Per-card actions: which card's ⋮ menu is open, which order is being moved
  // (drives the folder picker), and which order is mid-delete.
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [moveFor, setMoveFor] = useState<Order | null>(null);
  const [moving, setMoving] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [f, o] = await Promise.all([
        apiService.getUserFolders(userId).catch(() => [] as Folder[]),
        apiService.getUserOrders(activeFolder ?? userId, userId).catch(() => [] as Order[]),
      ]);
      setFolders(f);
      setOrders(
        [...o].sort((a, b) => (b.orderDate || '').localeCompare(a.orderDate || ''))
      );
    } finally {
      setLoading(false);
    }
  }, [userId, activeFolder]);

  useEffect(() => { void load(); }, [load]);

  // Poll refresh = ORDER STATUSES only. Folders don't change between ticks, so the live-refresh
  // loop doesn't re-pull get-folders every cycle (they're still loaded on mount + folder
  // mutations). Also never clears the grid on a transient failure — keeps the last good list.
  const loadOrders = useCallback(async () => {
    if (!userId) return;
    try {
      const o = await apiService.getUserOrders(activeFolder ?? userId, userId);
      setOrders([...o].sort((a, b) => (b.orderDate || '').localeCompare(a.orderDate || '')));
    } catch { /* transient — keep the current list */ }
  }, [userId, activeFolder]);

  // Live-refresh the grid while any video is still planning/generating, so
  // status badges and thumbnails update without a manual reload.
  // Poll ONLY while a video is actually rendering — NOT 'chat_active' (a draft state that
  // persists forever, which made this re-fetch get-files perpetually). The grid still reloads on
  // mount/navigation, so drafts show up; they just don't drive an endless background poll.
  const anyActive = orders.some((o) => ['in_progress', 'pending'].includes(o.orderStatus));
  useEffect(() => {
    if (!anyActive) return;
    // Pause while backgrounded; resume on the next tick when the tab is visible again.
    const t = setInterval(() => { if (!document.hidden) void loadOrders(); }, 12000);
    return () => clearInterval(t);
  }, [anyActive, loadOrders]);

  const FILTER_MATCH: Record<string, (o: Order) => boolean> = {
    all: () => true,
    drafts: (o) => o.orderStatus === 'chat_active' || o.orderStatus === 'preview',
    rendering: (o) => o.orderStatus === 'in_progress' || o.orderStatus === 'pending',
    done: (o) => o.orderStatus === 'completed',
    failed: (o) => o.orderStatus === 'failed',
  };

  const shown = useMemo(() => {
    let list = orders.filter(FILTER_MATCH[statusFilter]);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((o) => (o.orderName || '').toLowerCase().includes(q));
    }
    if (sortDir === 'oldest') list = [...list].reverse();
    return list;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, statusFilter, search, sortDir]);

  // Close any open ⋮ menu when clicking elsewhere or pressing Escape.
  useEffect(() => {
    if (!menuFor) return;
    const close = () => setMenuFor(null);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuFor(null); };
    window.addEventListener('click', close);
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('click', close); window.removeEventListener('keydown', onKey); };
  }, [menuFor]);

  const deleteOne = async (order: Order) => {
    setMenuFor(null);
    if (!await vConfirm(t('projects.confirmDelete', { name: order.orderName || t('projects.thisVideo') }))) return;
    setDeleting(order.orderId);
    try {
      await apiService.deleteOrder(order.orderId);
      await load();
      toast.success(t('projects.videoDeleted'));
    } catch {
      toast.error(t('projects.deleteFailed'));
    } finally {
      setDeleting(null);
    }
  };

  // Organize: move ONE project into a folder (or out of folders).
  const moveOne = async (targetFolderId: string) => {
    if (!moveFor || moving) return;
    const dest = targetFolderId === userId ? t('projects.recent') : (folders.find((f) => f.folderId === targetFolderId)?.folderName || t('projects.folder'));
    setMoving(true);
    try {
      await apiService.moveOrder(moveFor.orderId, targetFolderId);
      setMoveFor(null);
      await load();
      toast.success(t('projects.movedTo', { dest }));
    } catch {
      toast.error(t('projects.moveFailed'));
    } finally { setMoving(false); }
  };

  const handleCreateFolder = async () => {
    if (!userId) return;
    const name = await vPrompt(t('projects.folderName'));
    if (!name?.trim()) return;
    try {
      await apiService.createFolder(name.trim(), userId);
      await load();
      toast.success(t('projects.folderCreated', { name: name.trim() }));
    } catch {
      toast.error(t('projects.createFolderFailed'));
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 sm:py-10">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-6">
        <div className="min-w-0">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-text-primary">{t('projects.title')}</h1>
          {orders.length > 0 && (
            <p className="text-xs text-text-tertiary mt-1">
              {t('projects.subtitle', { count: orders.length })}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setSortDir((d) => (d === 'newest' ? 'oldest' : 'newest'))}
            className="flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-full glass text-sm text-text-primary hover:bg-surface-light transition-colors"
            title={sortDir === 'newest' ? t('projects.newest') : t('projects.oldest')}
          >
            <ChevronDown className={cn('w-4 h-4 shrink-0 transition-transform', sortDir === 'oldest' && 'rotate-180')} />
            <span className="hidden sm:inline">{sortDir === 'newest' ? t('projects.newest') : t('projects.oldest')}</span>
          </button>
          <button
            onClick={() => setSearchOpen((v) => { if (v) setSearch(''); return !v; })}
            className={cn('w-10 h-10 rounded-full glass flex items-center justify-center hover:bg-surface-light transition-colors shrink-0', searchOpen && 'border border-primary/40')}
          >
            <Search className="w-4 h-4 text-text-secondary" />
          </button>
        </div>
      </div>

      {/* Status filter — Draft / Rendering / Generated at a glance. Scrolls
          horizontally on mobile instead of wrapping into a tall block. */}
      <div className="flex items-center gap-2 mb-8 overflow-x-auto scrollbar-hide sm:flex-wrap pb-1 -mx-4 px-4 sm:mx-0 sm:px-0">
        {([
          ['all', t('projects.filterAll')],
          ['drafts', t('projects.filterDrafts')],
          ['rendering', t('projects.rendering')],
          ['done', t('projects.filterGenerated')],
          ['failed', t('projects.failed')],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setStatusFilter(key)}
            className={cn(
              'px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap shrink-0 transition-colors',
              statusFilter === key
                ? 'bg-primary/15 border border-primary/40 text-primary'
                : 'glass text-text-secondary hover:text-text-primary'
            )}
          >
            {label}
            {key !== 'all' && (
              <span className="ml-1.5 text-xs opacity-70">{orders.filter(FILTER_MATCH[key]).length}</span>
            )}
          </button>
        ))}
      </div>

      {searchOpen && (
        <div className="mb-8 animate-slide-up">
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('projects.searchPlaceholder')}
            className="w-full bg-surface-light border border-glass-border rounded-2xl px-5 py-3 text-sm text-text-primary placeholder:text-text-tertiary outline-none focus:border-primary/50"
          />
        </div>
      )}

      {/* Folders */}
      <div className="flex items-center gap-3 mb-6">
        <h2 className="text-lg font-bold text-text-primary">{t('projects.folders')}</h2>
        <button
          onClick={handleCreateFolder}
          className="w-9 h-9 rounded-full glass flex items-center justify-center hover:bg-surface-light transition-colors"
          title={t('projects.createFolder')}
        >
          <FolderPlus className="w-4 h-4 text-text-secondary" />
        </button>
      </div>
      {(folders.length > 0 || activeFolder) && (
        <div className="flex flex-wrap gap-2 mb-12">
          <button
            onClick={() => setActiveFolder(null)}
            className={cn(
              'flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors',
              activeFolder === null
                ? 'bg-primary/15 border border-primary/40 text-text-primary'
                : 'glass text-text-secondary hover:text-text-primary'
            )}
          >
            <FolderIcon className="w-4 h-4" />
            {t('projects.recent')}
          </button>
          {folders.map((f) => (
            <button
              key={f.folderId}
              onClick={() => setActiveFolder(f.folderId)}
              className={cn(
                'flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors',
                activeFolder === f.folderId
                  ? 'bg-primary/15 border border-primary/40 text-text-primary'
                  : 'glass text-text-secondary hover:text-text-primary'
              )}
            >
              <FolderIcon className="w-4 h-4" />
              {f.folderName}
              <span className="text-xs text-text-tertiary">{f.ordersCount || ''}</span>
            </button>
          ))}
        </div>
      )}

      {/* Videos */}
      <h2 className="text-lg font-bold text-text-primary mb-6">{t('projects.videos')}</h2>

      {loading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="animate-fade-in" style={{ animationDelay: `${i * 60}ms` }}>
              <div className="aspect-video rounded-2xl bg-surface-light/60 shimmer mb-3" />
              <div className="h-3.5 w-2/3 rounded-full bg-surface-light/60 shimmer mb-2" />
              <div className="h-3 w-1/3 rounded-full bg-surface-light/40 shimmer" />
            </div>
          ))}
        </div>
      )}

      {!loading && shown.length === 0 && (
        <p className="text-sm text-text-tertiary py-12 text-center">
          {orders.length === 0 ? t('projects.emptyNoVideos') : t('projects.emptyNoMatch')}
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {shown.map((order, i) => {
          const mediaUrl = order.outputUrl || order.videoUrl;
          const poster = order.previewImageUrl;
          const badge = statusBadge(order);
          const isAgent = order.operationType === 'heygen_chat';
          const isCompleted = order.orderStatus === 'completed';
          // Cover is themed by status (draft/rendering/failed); only a finished
          // video shows its own media. Drives the card's gradient + the StatusCover.
          const theme = thumbTheme(order.orderStatus);
          return (
            <div
              key={order.orderId}
              className="group cursor-pointer animate-fade-in"
              style={{ animationDelay: `${Math.min(i, 8) * 50}ms` }}
              // Hand the row's provider over: the project page's first load then
              // talks to the backend that owns this order instead of guessing.
              onClick={() => navigate(`/projects/${order.orderId}`, { state: { videoProvider: order.videoProvider ?? null } })}
            >
              <div
                className={cn(
                  'relative aspect-video rounded-2xl bg-gradient-to-br overflow-hidden mb-3',
                  'transition-all duration-300 group-hover:ring-2 group-hover:ring-primary/50',
                  'group-hover:shadow-glow group-hover:-translate-y-0.5',
                  theme.gradient
                )}
              >
                {order.isPreview && poster ? (
                  /* Free-tier preview: blurred result + lock — clicking opens
                     the project page where the subscribe gate lives. */
                  <>
                    <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ filter: 'blur(4px)', transform: 'scale(1.05)' }} />
                    <div className="absolute inset-0 bg-black/25 flex items-center justify-center">
                      <span className="w-12 h-12 rounded-full bg-black/50 border border-white/60 backdrop-blur-sm flex items-center justify-center">
                        <Play className="w-5 h-5 text-white fill-white ml-0.5" />
                      </span>
                    </div>
                  </>
                ) : isCompleted && mediaUrl ? (
                  /* Generated video — plays on hover (kept exactly as is). */
                  <video
                    src={mediaUrl}
                    poster={poster}
                    muted
                    playsInline
                    preload="metadata"
                    className="absolute inset-0 w-full h-full object-cover"
                    onMouseOver={(e) => void e.currentTarget.play().catch(() => undefined)}
                    onMouseOut={(e) => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
                  />
                ) : isCompleted && poster ? (
                  <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  /* Draft (purple) · Rendering (orange) · Failed (red) — consistent
                     themed cover, never gray. */
                  <StatusCover theme={theme} />
                )}
                {/* Per-card actions: ⋮ → Move / Delete */}
                <div className="absolute top-3 right-3 z-20" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => setMenuFor((cur) => (cur === order.orderId ? null : order.orderId))}
                    className="w-8 h-8 rounded-full bg-black/55 hover:bg-black/80 backdrop-blur-sm flex items-center justify-center text-white transition-colors"
                    aria-label={t('projects.projectOptions')}
                  >
                    {deleting === order.orderId ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreVertical className="w-4 h-4" />}
                  </button>
                  {menuFor === order.orderId && (
                    <div className="absolute top-9 right-0 w-44 rounded-xl bg-[#1b1b24] border border-white/10 shadow-2xl py-1 animate-fade-in">
                      <button
                        onClick={() => { setMenuFor(null); setMoveFor(order); }}
                        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-text-primary hover:bg-white/5 transition-colors"
                      >
                        <FolderIcon className="w-4 h-4 text-text-tertiary" /> {t('projects.moveToFolder')}
                      </button>
                      <button
                        onClick={() => void deleteOne(order)}
                        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" /> {t('projects.delete')}
                      </button>
                    </div>
                  )}
                </div>
                {isAgent && (
                  <span className="absolute bottom-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-black/70 text-xs font-medium text-white">
                    <MessageSquare className="w-3 h-3" />
                    {t('projects.agent')}
                  </span>
                )}
              </div>
              <h3 className="text-sm font-semibold text-text-primary truncate">{order.orderName}</h3>
              {/* Status under the name — colored dot + label, matching the
                  agentic-chat projects sidebar look (statusDotClass). */}
              <p className="text-xs mt-1 flex items-center gap-1.5 min-w-0">
                {badge && (
                  <>
                    <span className={cn('w-1.5 h-1.5 rounded-full shrink-0', statusDotClass(order.orderStatus))} />
                    <span className="text-text-secondary font-medium shrink-0">{badge}</span>
                    <span className="text-text-tertiary shrink-0">·</span>
                  </>
                )}
                <span className="text-text-tertiary truncate">{relativeTime(order.orderDate)} · {providerLabel(order)}</span>
              </p>
              {/* Team workspaces: creator attribution on org-owned projects */}
              {order.createdByName && (
                <p className="text-[11px] text-text-tertiary mt-0.5 truncate">
                  {t('team.byMember', { name: order.createdByName })}
                </p>
              )}
            </div>
          );
        })}
      </div>

      {/* Folder picker — organize one project */}
      {moveFor && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fade-in" onClick={() => setMoveFor(null)}>
          <div className="w-full max-w-sm rounded-3xl bg-[#15151d] border border-white/10 p-6 shadow-2xl animate-slide-up" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-text-primary mb-1 truncate">{t('projects.moveTitle', { name: moveFor.orderName || t('projects.project') })}</h3>
            <p className="text-xs text-text-tertiary mb-4">{t('projects.movePickFolder')}</p>
            <div className="space-y-1.5 max-h-64 overflow-y-auto">
              {userId && (
                <button
                  onClick={() => void moveOne(userId)}
                  disabled={moving}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-left transition-colors disabled:opacity-50"
                >
                  <FolderIcon className="w-4 h-4 text-text-tertiary" />
                  <span className="text-sm text-text-primary">{t('projects.noFolderRecent')}</span>
                </button>
              )}
              {folders.map((f) => (
                <button
                  key={f.folderId}
                  onClick={() => void moveOne(f.folderId)}
                  disabled={moving}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-left transition-colors disabled:opacity-50"
                >
                  <FolderIcon className="w-4 h-4 text-primary" />
                  <span className="text-sm text-text-primary truncate">{f.folderName}</span>
                </button>
              ))}
              {!folders.length && (
                <p className="text-xs text-text-tertiary px-1 py-2">{t('projects.noFoldersYet')}</p>
              )}
            </div>
            <button
              onClick={() => { const o = moveFor; setMoveFor(null); void handleCreateFolder().then(() => setMoveFor(o)); }}
              className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-dashed border-glass-border text-sm text-text-secondary hover:text-primary hover:border-primary/40 transition-colors"
            >
              <FolderPlus className="w-4 h-4" /> {t('projects.newFolder')}
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
